import { useRef, useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import LogoSymbol from '../../logo_symbol.png';
import { AimdocLogo } from '../brand/AimdocLogo';
import toastConfig from '../config/Toast';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useApi from '../api';
import { supabase } from '../supabaseClient'
import { useUser } from '../user/UserContext';
import useAnalytics from '../analytics/analytics';
import { conLog } from '../utils/conLog';
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuLabel,
   DropdownMenuSeparator,
   DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { BarChart3, Blocks, Bot, Briefcase, Building2, ChevronRight, ChevronsUpDown, Eye, Home, Library, LogOut, Menu, MessagesSquare, MoonIcon, BookOpen, Settings, SunIcon, UserCog, UserPlus, UserRoundCheck, Workflow, type LucideIcon } from 'lucide-react';
import { AimdocComponentContext } from '@aimdoc/sdk-react';
import { LicenseType } from '../types';
import { useTheme } from '../ThemeSwitcher';
import InviteModal from '../modals/InviteModal';
import GettingStartedCard from './GettingStartedCard';
import CreditBanner from '../billing/CreditBanner';

interface NavLinkItem {
   id: string;
   href: string;
   label: string;
   icon: LucideIcon;
   isActive: (selectedItem: string) => boolean;
   extraComponent?: JSX.Element | null;
   requiredLicense?: LicenseType[] | null;
}

interface NavGroup {
   title: string | null;
   items: NavLinkItem[];
}

const COLLAPSED_GROUPS_KEY = "aimdoc-sidebar-collapsed-groups";

const baseNavGroups: NavGroup[] = [
   {
      title: null,
      items: [
         {
            id: "home",
            href: "/",
            label: "Home",
            icon: Home,
            isActive: (selectedItem: string) =>
               selectedItem === "" || selectedItem === "/" || selectedItem === "home",
         },
      ],
   },
   {
      title: "Workspace",
      items: [
         {
            id: "agents",
            href: "/agents",
            label: "Agents",
            icon: Bot,
            isActive: (selectedItem: string) => selectedItem.includes("agents"),
            extraComponent: (
               <AimdocComponentContext
                  componentDescription="This button navigates to the Agents list page. You'll need to here to do anything agent related, such as training an agent on a website or file, updating the agent's form (like adding a new field), modifying the agent's persona or goal, customizing the agent's logo or icon, or creating a new agent."
                  componentName="Agents"
               />
            ),
         },
         {
            id: "services",
            href: "/services",
            label: "Services",
            icon: Workflow,
            isActive: (selectedItem: string) => selectedItem.includes("services"),
         },
         {
            id: "jobs",
            href: "/jobs",
            label: "Jobs",
            icon: Briefcase,
            isActive: (selectedItem: string) => selectedItem.includes("jobs"),
         },
         {
            id: "contacts",
            href: "/contacts",
            label: "Contacts",
            icon: UserRoundCheck,
            isActive: (selectedItem: string) => selectedItem.includes("contacts"),
         },
         {
            id: "accounts",
            href: "/accounts",
            label: "Accounts",
            icon: Building2,
            isActive: (selectedItem: string) => selectedItem.includes("accounts"),
         },
      ],
   },
   {
      title: "Activity",
      items: [
         {
            id: "sessions",
            href: "/sessions",
            label: "Sessions",
            icon: MessagesSquare,
            isActive: (selectedItem: string) => selectedItem.includes("sessions"),
         },
         {
            id: "liveview",
            href: "/liveview",
            label: "Visitors",
            icon: Eye,
            isActive: (selectedItem: string) => selectedItem.includes("liveview"),
         },
         {
            id: "analytics",
            href: "/analytics",
            label: "Analytics",
            icon: BarChart3,
            isActive: (selectedItem: string) => selectedItem.includes("analytics"),
         },
      ],
   },
   {
      title: "Configure",
      items: [
         {
            id: "integrations",
            href: "/integrations",
            label: "Integrations",
            icon: Blocks,
            isActive: (selectedItem: string) => selectedItem.includes("integrations"),
         },
         {
            id: "knowledge",
            href: "/knowledge",
            label: "Knowledge Base",
            icon: Library,
            isActive: (selectedItem: string) => selectedItem.includes("knowledge"),
         },
      ],
   },
];

const adminNavLink: NavLinkItem = {
   id: "admin",
   href: "/admin",
   label: "Admin",
   icon: UserCog,
   isActive: (selectedItem: string) => selectedItem.includes("admin"),
};

const NavBar = ({ selectedItem, onAddCredits }: any) => {
   const [inviteModalVisible, setInviteModalVisible] = useState(false);
   const { connectWebSocket } = useApi();
   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
   const [isNavHovered, setIsNavHovered] = useState(false);
   const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
   const navigate = useNavigate();
   const location = useLocation();
   const { user, organization, refresh } = useUser();
   const { captureEvent } = useAnalytics();
   const shouldReconnectRef = useRef(true);
   const socketRef = useRef<WebSocket | null>(null);
   const reconnectAttemptsRef = useRef(0);
   const { isDark, setIsDark } = useTheme();

   // Check if we're on an agent builder route (agents/:id but not agents/new)
   const isAgentBuilderRoute = location.pathname.match(/^\/agents\/[^\/]+$/) || location.pathname.match(/^\/sessions\/[^\/]+$/);
   const isSessionDetailRoute = !!location.pathname.match(/^\/sessions\/[^\/]+$/);

   const search = new URLSearchParams(window.location.search);
   const sessionId = search.get('session_id');
   conLog('sessionId:', sessionId)
   useEffect(() => {
      if (sessionId) {
         refresh();
      }
   }, [sessionId, refresh]);

   const handleLogout = async () => {
      await supabase.auth.signOut();
      captureEvent('logged_out', {});
      navigate('/login');
   };




   const isLicensed = (item: NavLinkItem) => {
      if (!item.requiredLicense) {
         return true;
      }
      if (!organization) {
         return false;
      }
      const currentLicense = organization.license_type as LicenseType;
      return item.requiredLicense.includes(currentLicense);
   };

   const navGroups: NavGroup[] = baseNavGroups
      .map(group => {
         const items = group.items.filter(isLicensed);
         if (group.title === "Configure" && user?.is_admin) {
            return { ...group, items: [...items, adminNavLink] };
         }
         return { ...group, items };
      })
      .filter(group => group.items.length > 0);

   useEffect(() => {
      try {
         const stored = localStorage.getItem(COLLAPSED_GROUPS_KEY);
         if (stored) setCollapsedGroups(JSON.parse(stored));
      } catch {
         // ignore malformed storage
      }
   }, []);

   const toggleGroup = (title: string) => {
      setCollapsedGroups(prev => {
         const next = { ...prev, [title]: !prev[title] };
         try {
            localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify(next));
         } catch {
            // storage unavailable
         }
         return next;
      });
   };

   const toggleMobileMenu = () => {
      setMobileMenuOpen(!mobileMenuOpen);
   };

   useEffect(() => {
      if (organization?.id) {
         // Conversations that started while this tab was hidden, keyed by id so
         // repeats collapse. Rendered as ONE rollup toast with a stable id, so
         // each update replaces the previous one instead of stacking.
         const pendingNotifications: Map<string, any> = new Map();
         const AWAY_TOAST_ID = 'sessions-started-while-away';

         const handleVisibilityChange = () => {
            if (!document.hidden && pendingNotifications.size > 0) {
               const count = pendingNotifications.size;
               pendingNotifications.clear();

               toast.info(
                  `${count} Session${count > 1 ? 's' : ''} Started While Away`,
                  {
                     ...toastConfig,
                     toastId: AWAY_TOAST_ID,
                     autoClose: 10000,
                     description: `${count > 1 ? 'Multiple sessions' : 'A session'} started while you were away`,
                     action: {
                        label: 'View sessions',
                        onClick: () => navigate('/sessions')
                     },
                  }
               );
            }
         };

         const initializeWebSocket = async () => {
            try {
               const ws = await connectWebSocket(`/ws/organization/${organization.id}`);
               if (!ws) {
                  console.error('WebSocket connection failed');
                  return;
               }
               socketRef.current = ws;

               ws.onmessage = (event) => {
                  try {
                     const data = JSON.parse(event.data);
                     if (data.type === 'session_flagged') {
                        toast.warning(
                           'Session flagged',
                           {
                              ...toastConfig,
                              autoClose: 12000,
                              description: data.agent_name
                                 ? `Session moderation flagged a visitor on ${data.agent_name}`
                                 : 'Session moderation flagged a visitor',
                              action: {
                                 label: 'Review session',
                                 onClick: () => navigate(`/sessions/${data.conversation_id}`)
                              },
                           }
                        );
                        return;
                     }
                     if (data.type === 'session_active') {
                        // The same event also fires when an existing conversation
                        // goes inactive -> active again (visitor idles, comes back).
                        // Live views consume that for status; it is not a new
                        // session and must not toast.
                        if (data.resumed) return;

                        const isPageVisible = !document.hidden;

                        if (isPageVisible) {
                           toast.info(
                              'New active session',
                              {
                                 ...toastConfig,
                                 // One toast per conversation: a repeat updates in place.
                                 toastId: `session-active-${data.conversation_id}`,
                                 autoClose: 8000,
                                 description: 'A new session has started',
                                 action: {
                                    label: 'View session',
                                    onClick: () => navigate(`/sessions/${data.conversation_id}`)
                                 },
                              }
                           );
                        } else {
                           pendingNotifications.set(data.conversation_id, data);

                           toast.info(
                              `${pendingNotifications.size} New Session${pendingNotifications.size > 1 ? 's' : ''}`,
                              {
                                 ...toastConfig,
                                 toastId: AWAY_TOAST_ID,
                                 // Stays until the tab is visible again (then the
                                 // rollup above replaces it), but is always closable.
                                 autoClose: false,
                                 hideProgressBar: true,
                                 description: pendingNotifications.size > 1
                                    ? 'Sessions started while this tab was inactive'
                                    : 'A session started while this tab was inactive',
                                 action: {
                                    label: 'View sessions',
                                    onClick: () => navigate('/sessions')
                                 },
                                 onClose: () => pendingNotifications.clear(),
                              }
                           );
                        }
                     }
                  } catch (error) {
                     console.error('Error parsing WebSocket message:', error);
                  }
               };

               document.addEventListener('visibilitychange', handleVisibilityChange);

               ws.onopen = () => {
                  reconnectAttemptsRef.current = 0;
               };

               ws.onclose = () => {
                  if (shouldReconnectRef.current) {
                     const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 30000);
                     setTimeout(() => {
                        reconnectAttemptsRef.current += 1;
                        initializeWebSocket();
                     }, delay);
                  }
               };

               ws.onerror = (error) => {
                  console.error('WebSocket error:', error);
               };
            } catch (error) {
               console.error('Failed to establish WebSocket connection:', error);
               if (shouldReconnectRef.current) {
                  const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 30000);
                  setTimeout(() => {
                     reconnectAttemptsRef.current += 1;
                     initializeWebSocket();
                  }, delay);
               }
            }
         };

         initializeWebSocket();

         return () => {
            shouldReconnectRef.current = false;
            if (socketRef.current?.readyState === WebSocket.OPEN) {
               socketRef.current.send(JSON.stringify({ type: 'silent_close_connection' }));
               socketRef.current.close();
            }
            socketRef.current = null;
            // Clean up visibility change listener
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            // A persistent "while away" toast must not outlive the socket that
            // owns it (org switch / unmount), or it lingers with nothing to replace it.
            toast.dismiss(AWAY_TOAST_ID);
            pendingNotifications.clear();
         };
      }
   }, [organization?.id]);

   useEffect(() => {
      const handleVisibilityChange = () => {
         if (document.visibilityState === 'visible' && !socketRef.current) {
            reconnectAttemptsRef.current = 0;
         }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
         document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
   }, []);



   const userDisplayName = [
      user?.first_name,
      user?.last_name,
   ].filter(Boolean).join(' ') || user?.username || user?.email?.split('@')[0] || 'User';
   const userInitials = userDisplayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase();

   const isRailCollapsed = Boolean(isAgentBuilderRoute && !isNavHovered);

   const navItemClass = (active: boolean) =>
      `${isRailCollapsed ? 'w-8 justify-center px-0' : 'w-full gap-2.5 px-3'} relative flex h-8 items-center rounded-md text-[13px] font-medium transition-all duration-300 ease-in-out ${
         active
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
      }`;

   const navLabelClass = `whitespace-nowrap transition-all duration-300 ease-in-out ${
      isRailCollapsed ? 'absolute w-0 overflow-hidden opacity-0' : 'opacity-100'
   }`;

   const secondaryActions = [
      {
         id: 'documentation',
         label: 'Documentation',
         icon: BookOpen,
         onClick: () => window.open('https://docs.aimdoc.ai/', '_blank'),
      },
      { id: 'invite', label: 'Invite Teammates', icon: UserPlus, onClick: () => setInviteModalVisible(true) },
   ];

   const renderUserMenu = (compact: boolean) => (
      <DropdownMenu>
         <DropdownMenuTrigger asChild>
            <button
               type="button"
               aria-label="Open user menu"
               className={`group flex items-center rounded-lg text-left outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring ${
                  compact ? 'h-9 w-9 justify-center' : 'w-full gap-3 p-2'
               }`}
            >
               <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-[11px] font-semibold tracking-wide text-foreground/75">
                  {user?.profile_photo_url ? (
                     <img
                        src={user.profile_photo_url}
                        alt=""
                        className="h-full w-full object-cover"
                     />
                  ) : (
                     userInitials
                  )}
               </div>

               {!compact && (
                  <>
                     <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">
                           {userDisplayName}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                           {organization?.name || user?.email}
                        </p>
                     </div>
                     <ChevronsUpDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                  </>
               )}
            </button>
         </DropdownMenuTrigger>

         <DropdownMenuContent
            side="top"
            align="start"
            sideOffset={8}
            className="w-60 rounded-lg p-1.5"
         >
            <DropdownMenuLabel className="px-2 py-2 font-normal">
               <p className="truncate text-sm font-medium text-foreground">{userDisplayName}</p>
               {user?.email && (
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{user.email}</p>
               )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
               className="cursor-pointer rounded-md py-2"
               onSelect={() => navigate('/user-settings')}
            >
               <Settings />
               <span>Settings</span>
            </DropdownMenuItem>
            <DropdownMenuItem
               className="cursor-pointer rounded-md py-2"
               onSelect={() => setIsDark(!isDark)}
            >
               {isDark ? <MoonIcon /> : <SunIcon />}
               <span>Appearance</span>
               <span className="ml-auto text-xs text-muted-foreground">
                  {isDark ? 'Dark' : 'Light'}
               </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
               className="cursor-pointer rounded-md py-2 text-destructive focus:text-destructive"
               onSelect={handleLogout}
            >
               <LogOut />
               <span>Log out</span>
            </DropdownMenuItem>
            <AimdocComponentContext
               componentDescription="This menu contains user settings, appearance, and log out controls."
               componentName="User Menu"
            />
         </DropdownMenuContent>
      </DropdownMenu>
   );

   // Add CSS to document head for main content margin
   useEffect(() => {
      const styleId = 'navbar-responsive-styles';
      let existingStyle = document.getElementById(styleId);
      
      if (!existingStyle) {
         const style = document.createElement('style');
         style.id = styleId;
         document.head.appendChild(style);
         existingStyle = style;
      }

      if (isAgentBuilderRoute) {
         existingStyle.textContent = `
            .main-content-area {
               margin-left: ${isNavHovered ? '224px' : '56px'};
               transition: margin-left 300ms ease-in-out;
            }
            @media (max-width: 768px) {
               .main-content-area {
                  margin-left: 0 !important;
               }
            }
         `;
      } else {
         existingStyle.textContent = `
            .main-content-area {
               margin-left: 224px;
               transition: margin-left 300ms ease-in-out;
            }
            @media (max-width: 768px) {
               .main-content-area {
                  margin-left: 0 !important;
               }
            }
         `;
      }

      return () => {
         // Cleanup when component unmounts
         const style = document.getElementById(styleId);
         if (style) {
            style.remove();
         }
      };
   }, [isAgentBuilderRoute, isNavHovered]);

   return (
      <>
         {!isSessionDetailRoute && (
            <button
               onClick={toggleMobileMenu}
               className="fixed top-4 left-4 p-2 rounded-lg bg-background border md:hidden z-30"
            >
               <Menu className="h-6 w-6" />
            </button>
         )}

         <div className="bg-background">
            <aside 
               id="sidebar" 
               className={`fixed ${mobileMenuOpen ? 'block' : 'hidden'} md:block z-20 h-full top-0 left-0 flex-shrink-0 flex-col transition-all duration-300 ease-in-out ${
                  isAgentBuilderRoute 
                     ? (isNavHovered ? 'w-56' : 'w-14') 
                     : 'w-56'
               }`}
               onMouseEnter={() => setIsNavHovered(true)}
               onMouseLeave={() => setIsNavHovered(false)}
            >
               <div className="bg-background relative flex-1 flex flex-col h-full border-r pt-0 transition-all duration-300 ease-in-out">
                  <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto scrollbar-hidden transition-all duration-300 ease-in-out overflow-x-hidden">
                     <a href="/" className="flex items-center w-full justify-center">
                        {isAgentBuilderRoute && !isNavHovered ? (
                           <img src={LogoSymbol} className="h-7 justify-center transition-all duration-300 ease-in-out" alt="Logo" />
                        ) : (
                           <AimdocLogo className="h-7 w-auto text-foreground transition-all duration-300 ease-in-out" />
                        )}
                     </a>
                     <nav className="mt-6 flex flex-1 flex-col px-3 transition-all duration-300 ease-in-out">
                        {navGroups.map(group => {
                           const isCollapsed = Boolean(group.title && collapsedGroups[group.title]);
                           const containsActive = group.items.some(item => item.isActive(selectedItem));
                           const showItems = isRailCollapsed || !isCollapsed;

                           return (
                              <div key={group.title ?? 'primary'} className="mb-2 flex flex-col gap-0.5">
                                 {group.title && !isRailCollapsed && (
                                    <button
                                       type="button"
                                       onClick={() => toggleGroup(group.title as string)}
                                       className={`flex w-full items-center gap-1 rounded-md px-3 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                                          containsActive && isCollapsed
                                             ? 'text-foreground/80'
                                             : 'text-muted-foreground/70 hover:text-foreground/80'
                                       }`}
                                    >
                                       <ChevronRight
                                          className={`size-3 shrink-0 transition-transform ${!isCollapsed ? 'rotate-90' : ''}`}
                                       />
                                       {group.title}
                                    </button>
                                 )}

                                 {showItems && group.items.map(item => {
                                    const Icon = item.icon;
                                    return (
                                       <Link
                                          key={item.id}
                                          id={item.id}
                                          to={item.href}
                                          className={navItemClass(item.isActive(selectedItem))}
                                       >
                                          <Icon className="size-4 shrink-0 opacity-70" strokeWidth={1.75} />
                                          <span className={navLabelClass}>{item.label}</span>
                                          {item.extraComponent && !isRailCollapsed && item.extraComponent}
                                       </Link>
                                    );
                                 })}
                              </div>
                           );
                        })}

                        <div className="mt-auto flex flex-col gap-0.5 pt-2">
                           {secondaryActions.map(action => {
                              const Icon = action.icon;
                              return (
                                 <button
                                    key={action.id}
                                    type="button"
                                    onClick={action.onClick}
                                    className={navItemClass(false)}
                                 >
                                    <Icon className="size-4 shrink-0 opacity-70" strokeWidth={1.75} />
                                    <span className={navLabelClass}>{action.label}</span>
                                 </button>
                              );
                           })}
                           {onAddCredits && (
                              <div className="pt-1.5">
                                 <CreditBanner onAddCredits={onAddCredits} collapsed={isRailCollapsed} />
                              </div>
                           )}
                           <div className="pt-1.5">
                              <GettingStartedCard collapsed={isRailCollapsed} />
                           </div>
                        </div>
                     </nav>

                     <div className={`mt-3 border-t border-border px-2 pt-3 transition-all duration-300 ease-in-out ${
                        isAgentBuilderRoute && !isNavHovered ? 'flex justify-center' : ''
                     }`}>
                        {renderUserMenu(Boolean(isAgentBuilderRoute && !isNavHovered))}
                     </div>

                  </div>
               </div>
            </aside>

            {mobileMenuOpen && (
               <div
                  className="fixed inset-0 bg-background md:hidden z-10 mb-16 w-full"
                  onClick={() => setMobileMenuOpen(false)}
               />
            )}
         </div>

         <InviteModal
            isOpen={inviteModalVisible}
            onClose={() => setInviteModalVisible(false)}
         />

         <script async defer src="https://buttons.github.io/buttons.js"></script>
      </>
   );
};

export default NavBar;
